import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { clearCart } from "./cartSlice";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

const STORAGE_KEY = "vb_auth";

// ── localStorage helpers ──────────────────────────────────────────────

const persistAuth = (user, accessToken) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ user, accessToken, ts: Date.now() })
    );
  } catch {}
};

const loadAuth = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 3 hours (matches server token lifetime)
    if (Date.now() - data.ts > 3 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const clearAuth = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

// ── Hydrate initial state from localStorage ───────────────────────────

const cached = loadAuth();

const initialState = {
  user: cached?.user || null,
  accessToken: cached?.accessToken || null,
  isAuthenticated: !!cached?.user,
  loading: false,
  error: null,
  justRegistered: false,
  justLoggedIn: false,
  hydrated: !!cached,
};

// ── Async thunks ──────────────────────────────────────────────────────

export const registerUser = createAsyncThunk(
  "auth/register",
  async (formData, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(`${USER_API}/api/auth/register`, formData, {
        withCredentials: true,
      });
      dispatch(clearCart()); // new account starts with empty cart
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { error: "Registration failed" }
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${USER_API}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { error: "Login failed" }
      );
    }
  }
);

export const googleAuth = createAsyncThunk(
  "auth/googleAuth",
  async ({ credential }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${USER_API}/api/auth/google`,
        { credential },
        { withCredentials: true }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { error: "Google sign-in failed" }
      );
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refresh",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${USER_API}/api/auth/refresh`,
        {},
        { withCredentials: true }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { error: "Token refresh failed" }
      );
    }
  }
);

export const fetchProfile = createAsyncThunk(
  "auth/fetchProfile",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const res = await axios.get(`${USER_API}/api/users/me`, {
        withCredentials: true,
        headers: auth.accessToken
          ? { Authorization: `Bearer ${auth.accessToken}` }
          : {},
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { error: "Failed to fetch profile" }
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { getState, dispatch }) => {
    try {
      const { auth } = getState();
      await axios.post(
        `${USER_API}/api/auth/logout`,
        {},
        {
          withCredentials: true,
          headers: auth.accessToken
            ? { Authorization: `Bearer ${auth.accessToken}` }
            : {},
        }
      );
    } catch {
      // Logout even if server call fails
    }
    clearAuth();
    dispatch(clearCart()); // clear cart on sign out
    return null;
  }
);

// ── Slice ─────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError(state) {
      state.error = null;
    },
    clearAuthFlash(state) {
      state.justRegistered = false;
      state.justLoggedIn = false;
    },
  },
  extraReducers: (builder) => {
    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.justRegistered = true;
        state.error = null;
        persistAuth(action.payload.user, action.payload.access_token);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Registration failed";
      });

    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.justLoggedIn = true;
        state.error = null;
        persistAuth(action.payload.user, action.payload.access_token);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Login failed";
      });

    // Google OAuth
    builder
      .addCase(googleAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        state.justLoggedIn = !action.payload.is_new_user;
        state.justRegistered = !!action.payload.is_new_user;
        state.error = null;
        persistAuth(action.payload.user, action.payload.access_token);
      })
      .addCase(googleAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Google sign-in failed";
      });

    // Refresh
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.access_token;
        state.isAuthenticated = true;
        persistAuth(action.payload.user, action.payload.access_token);
      })
      .addCase(refreshToken.rejected, (state) => {
        // Only clear if we don't have a cached session
        // This prevents logout on failed refresh when localStorage is still valid
        if (!loadAuth()) {
          state.user = null;
          state.accessToken = null;
          state.isAuthenticated = false;
        }
      });

    // Fetch profile
    builder
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = true;
        if (state.accessToken) {
          persistAuth(action.payload.user, state.accessToken);
        }
      })
      .addCase(fetchProfile.rejected, (state) => {
        // Don't nuke session on profile fetch failure if localStorage is valid
        if (!loadAuth()) {
          state.user = null;
          state.accessToken = null;
          state.isAuthenticated = false;
        }
      });

    // Logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.justRegistered = false;
      state.justLoggedIn = false;
    });
  },
});

export const { clearAuthError, clearAuthFlash } = authSlice.actions;
export default authSlice.reducer;
